import { memo, useMemo } from 'react'
import { MapMarker } from '@wehunt/map'
import { DEFAULT_MAP_APPEARANCE } from '../../lib/mapAppearance'
import type { ReplayMapPin } from '../../lib/replayTypes'
import type { MapObject } from '../../lib/types'
import { PinMarkerContent } from '../PinMarkerContent'

export interface ReplayMapPinLayerProps {
  mapPins: readonly ReplayMapPin[]
  /** Live map zoom — drives the label size. */
  zoom: number
}

/**
 * The hunt's static map pins, in place for the whole replay — the same marker the
 * team map draws, so a stand reads as a stand on both.
 *
 * Memoized and handed only `mapPins` and `zoom`, never the cursor: the viewer
 * re-renders on every playback frame, and these pins never move, so rebuilding
 * every marker per frame would put per-frame work back on React that the viewer
 * otherwise keeps off it (the cursor ref and the canvas).
 */
export const ReplayMapPinLayer = memo(function ReplayMapPinLayer({
  mapPins,
  zoom,
}: ReplayMapPinLayerProps) {
  const objects = useMemo<MapObject[]>(
    () =>
      mapPins.map((pin) => ({
        id: pin.id,
        type: pin.pinType,
        lat: pin.lat,
        lon: pin.lon,
        label: pin.name ?? '',
        color: pin.color,
        customSymbol: pin.customSymbol,
        description: pin.description,
      })),
    [mapPins],
  )

  return (
    <>
      {objects.map((object) => (
        <MapMarker key={object.id} lat={object.lat} lon={object.lon} anchor="bottom">
          <PinMarkerContent
            object={object}
            appearance={DEFAULT_MAP_APPEARANCE}
            zoom={zoom}
            pinNamesVisible
          />
        </MapMarker>
      ))}
    </>
  )
})
